import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAnalytics } from 'analytics';
import { ANALYTICS_EVENTS } from 'analytics/events';
import { onLogoutCleanup } from 'utils/logoutCleanup';
import { buildSearchUrl, type AmbossMode } from './amboss.config';
import { onAmbossDataReset } from './ambossDataReset';
import { useAmbossHistory, type AmbossHistoryEntry } from './useAmbossHistory';
import {
  AmbossContext,
  type AmbossContextValue,
  type AmbossOverlay,
  type AmbossScreen,
} from './useAmboss';

export function AmbossProvider({ children }: { children: React.ReactNode }) {
  const { trackEvent } = useAnalytics();
  const { groups: historyGroups, add, bump, remove, updateCid } = useAmbossHistory();

  const [screen, setScreen] = useState<AmbossScreen>('closed');
  const [overlay, setOverlay] = useState<AmbossOverlay>('none');
  const [query, setQuery] = useState('');
  const [mode, setMode] = useState<AmbossMode>('ai');
  // The cid of the ai-mode conversation being resumed, or null for a fresh one.
  const [cid, setCid] = useState<string | null>(null);
  const [submissionId, setSubmissionId] = useState(0);

  // The ai-mode history entry awaiting a cid from AMBOSS, or null.
  const currentAiEntryId = useRef<string | null>(null);

  // The provider sits above the router, so its state survives logout.
  useEffect(() => {
    const reset = () => {
      setScreen('closed');
      setOverlay('none');
      setQuery('');
      setMode('ai');
      setCid(null);
      currentAiEntryId.current = null;
    };
    const unsubscribeLogout = onLogoutCleanup(reset);
    const unsubscribeWipe = onAmbossDataReset(reset);
    return () => {
      unsubscribeLogout();
      unsubscribeWipe();
    };
  }, []);

  const open = useCallback(() => {
    setScreen('search');
    setOverlay('none');
    trackEvent(ANALYTICS_EVENTS.AMBOSS_OPENED);
  }, [trackEvent]);

  const submit = useCallback(
    (nextQuery: string, nextMode: AmbossMode) => {
      const trimmed = nextQuery.trim();
      if (!trimmed) return;
      setQuery(trimmed);
      setMode(nextMode);
      setCid(null);
      setSubmissionId((id) => id + 1);
      setScreen('embedded');
      setOverlay('none');
      const entryId = add(trimmed, nextMode);
      currentAiEntryId.current = nextMode === 'ai' ? entryId : null;
      trackEvent(
        nextMode === 'ai'
          ? ANALYTICS_EVENTS.AMBOSS_AIMODE_SENT
          : ANALYTICS_EVENTS.AMBOSS_SEARCH_SENT
      );
    },
    [add, trackEvent]
  );

  const back = useCallback(() => {
    setOverlay('none');
    setScreen('search');
  }, []);

  const showHelp = useCallback(() => setOverlay((o) => (o === 'help' ? 'none' : 'help')), []);
  const showHistory = useCallback(
    () => setOverlay((o) => (o === 'history' ? 'none' : 'history')),
    []
  );
  const hideOverlay = useCallback(() => setOverlay('none'), []);

  // Re-running bumps the existing entry to the top instead of adding a
  // duplicate. An ai-mode entry that captured a cid resumes that exact
  // conversation; anything else re-opens the query as a fresh search.
  const rerun = useCallback(
    (entry: AmbossHistoryEntry) => {
      bump(entry.id);
      setQuery(entry.query);
      setMode(entry.mode);
      setCid(entry.mode === 'ai' ? (entry.cid ?? null) : null);
      setSubmissionId((id) => id + 1);
      setScreen('embedded');
      setOverlay('none');
      currentAiEntryId.current = entry.mode === 'ai' ? entry.id : null;
      trackEvent(
        entry.mode === 'ai'
          ? ANALYTICS_EVENTS.AMBOSS_AIMODE_SENT
          : ANALYTICS_EVENTS.AMBOSS_SEARCH_SENT
      );
    },
    [bump, trackEvent]
  );
  const deleteEntry = useCallback((id: string) => remove(id), [remove]);

  // AMBOSS replaces ai-mode's `?q=` with a `?cid=` conversation id once the
  // conversation is created. The Electron <webview> surfaces that navigation to
  // AmbossEmbed, which reports the cid here so it is stored on the entry. The
  // browser iframe is cross-origin and reports nothing, so entries keep no cid.
  const reportCid = useCallback(
    (cid: string) => {
      const id = currentAiEntryId.current;
      if (id) updateCid(id, cid);
    },
    [updateCid]
  );

  const close = useCallback(() => {
    setScreen('closed');
    setOverlay('none');
    trackEvent(ANALYTICS_EVENTS.AMBOSS_CLOSED);
  }, [trackEvent]);

  const value = useMemo<AmbossContextValue>(() => {
    // Alive even while AMBOSS is closed, so AmbossLayer keeps a single
    // webview mounted instead of tearing it down and re-running SSO.
    const embedUrl = query.trim() ? buildSearchUrl(query, mode, cid ?? undefined) : null;
    return {
      screen,
      overlay,
      query,
      mode,
      embedUrl,
      submissionId,
      historyGroups,
      isOpen: screen !== 'closed',
      isAmbossMode: screen === 'embedded' || overlay !== 'none',
      open,
      submit,
      back,
      showHelp,
      showHistory,
      hideOverlay,
      rerun,
      deleteEntry,
      reportCid,
      close,
    };
  }, [
    screen,
    overlay,
    query,
    mode,
    cid,
    submissionId,
    historyGroups,
    open,
    submit,
    back,
    showHelp,
    showHistory,
    hideOverlay,
    rerun,
    deleteEntry,
    reportCid,
    close,
  ]);

  return <AmbossContext.Provider value={value}>{children}</AmbossContext.Provider>;
}
